import { Types } from 'mongoose';

import { getAttributionBasedLocation } from '@/components/common/MultiFlightMap/MultiFlightMap.helpers';

import type { MultiFlightEntityType } from 'sonata-static-storage/lib/common/common.types';
import type { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';
import type { ModifiedZone } from '@/components/common/MultiFlightMap/components/MapView/map.types';
import type { AttributionType, AttributionZones } from '@/components/common/MultiFlightMap/multiFlightMap.types';

export function disableAttributionLocations(
  attributionLocations: AttributionZones,
  selectedVenues: string[]
): {
  noAttributionIsCurrentlyEnabled: boolean;
  updatedAttributionLocations: AttributionZones;
} {
  const attributionLocationIdsToBeDisabled = Object.values(attributionLocations)
    .filter(aZ => selectedVenues.includes(aZ.shop!))
    .map(aZ => aZ._id!);
  const updatedAttributionLocations = Object.fromEntries(
    Object.entries(attributionLocations).map(([id, zone]) => [
      id,
      {
        ...zone,
        isModified: attributionLocationIdsToBeDisabled.includes(zone._id!) || !!zone.isModified,
        torusConfig:
          zone.torusConfig?.map(config => ({
            ...config,
            deleted: attributionLocationIdsToBeDisabled.includes(zone._id!) || config.deleted
          })) || []
      }
    ])
  ) as AttributionZones;
  return {
    noAttributionIsCurrentlyEnabled:
      attributionLocationIdsToBeDisabled.length === Object.values(attributionLocations).length,
    updatedAttributionLocations
  };
}

export function enableAttributionLocations(
  entityType: MultiFlightEntityType,
  entityId: string,
  attributionLocations: AttributionZones,
  venuesForAttribution: Partial<Venue>[],
  attributionMode?: AttributionType,
  attributionRadiusInMeters?: number
) {
  const neededVenuesIds = venuesForAttribution.map(v => v._id).filter(Boolean) as string[];
  const existingAttributionLocationsUntouched = Object.values(attributionLocations).filter(
    ({ shop }) => !neededVenuesIds.includes(shop!)
  );
  const existingAttributionLocationsToBeUpdated = Object.values(attributionLocations).filter(({ shop }) =>
    neededVenuesIds.includes(shop!)
  );
  const noneExistingVenues = neededVenuesIds.filter(
    vId => !existingAttributionLocationsToBeUpdated.some(attrZone => attrZone.shop === vId)
  );
  const newAttributionZones: Record<string, ModifiedZone> = noneExistingVenues.reduce((acc, venueId) => {
    const newAttrZoneId = new Types.ObjectId().toString();
    const associatedVenue = venuesForAttribution.find(v => v._id === venueId);
    return {
      ...acc,
      [newAttrZoneId]: {
        ...getAttributionBasedLocation(
          associatedVenue,
          entityType,
          entityId,
          attributionMode,
          attributionRadiusInMeters
        ),
        _id: newAttrZoneId,
        isModified: true
      }
    };
  }, {});
  const updatedExistingAttributionLocations = Object.values(existingAttributionLocationsToBeUpdated).reduce(
    (acc, attrLocation) => {
      return {
        ...acc,
        [attrLocation._id!]: {
          ...attrLocation,
          isModified: true,
          torusConfig: [
            {
              ...attrLocation.torusConfig![0],
              deleted: false
            }
          ]
        }
      };
    },
    {}
  );
  return {
    ...existingAttributionLocationsUntouched.reduce(
      (acc, attrLocation) => ({ ...acc, [attrLocation._id!]: attrLocation }),
      {}
    ),
    ...updatedExistingAttributionLocations,
    ...newAttributionZones
  };
}
